import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { ViolationFormRule, ViolationFormSchema } from "@core/form/form.rule";
import { useFormDefaultViolation } from "@core/form/form-default-violation.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateViolation, 
  useQyGetViolationById, 
  useQyDeleteViolation 
} from "@core/query/violation.query";
import { GetResponseViolationModel } from "@core/model/query-model-violation";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditViolation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultViolation();
  const navigate = useNavigate();
  const { mapUpdateViolation, mapDeleteViolation } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { violationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Violation
  const handleGetApiSuccess = (data: GetResponseViolationModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: violationResponse,
    isLoading,
    isError: violationError,
  } = useQyGetViolationById(violationId || "", handleGetApiSuccess);
  const violationData = violationResponse?.data?.[0];

  // API UPDATE Violation
  const handleUpdateApiSuccess = () => {
    showSuccess("Violation updated successfully");
  };
  const { mutate: updateViolation, isLoading: isEditLoading } = useQyUpdateViolation(
    handleUpdateApiSuccess
  );

  // API DELETE Violation
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Violation deleted successfully");
  };
  const { mutate: deleteViolation } = useQyDeleteViolation(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ViolationFormSchema>({
    defaultValues: getDefault(violationData),
    resolver: zodResolver(ViolationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ViolationFormSchema) => {
    if (!violationData) {
      throw new Error("No violation data");
    }

    const formData = mapUpdateViolation(form, violationData.code);
    updateViolation(formData);
  };
  const handleValidateError = (err: FieldErrors<ViolationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!violationData) {
      throw new Error("No violation data found");
    }

    const form = mapDeleteViolation(violationData.code);
    deleteViolation(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    violationId,
    violationData,
    isLoading,
    violationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}